import {ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {combineLatest, debounceTime, fromEvent} from 'rxjs';
import {TableElement, TableExportUtil, UnsubscribeOnDestroyAdapter,} from '@shared';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRippleModule} from '@angular/material/core';
import {FeatherIconsComponent} from '@shared/components/feather-icons/feather-icons.component';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatTableModule} from '@angular/material/table';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BreadcrumbComponent} from '@shared/components/breadcrumb/breadcrumb.component';
import {BusinessGroupService} from "@core/service/business-group.service";
import {UserService} from "@core/http/table-data-services/User.service";
import {UserDetails} from "@core/models/UserDetails";
import {FormatMobilePipe} from "@core/pipes/format-mobile.pipe";
import {Router} from "@angular/router";
import {UserDataSource} from "@core/data-source/UserDataSource";
import {DoDeleteComponent} from "@shared/components/do-delete/do-delete.component";
import {BranchGroupService} from "@core/service/branch-group.service";
import {TopWidgetsComponent} from "@shared/components/top-widgets/top-widgets.component";
import {HttpMesageHandlerService} from "@core/service/http-mesage-handler";
import {AuthManagementService} from "@core/service/auth-management.service";
import {NgIf} from "@angular/common";
import {distinctUntilChanged, tap} from 'rxjs/operators';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    FeatherIconsComponent,
    MatRippleModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    FormatMobilePipe,
    TopWidgetsComponent,
    NgIf
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit {

  // ═════════════════════════════════════════════════════════════════════════════
  // PROPIEDADES
  // ═════════════════════════════════════════════════════════════════════════════

  displayedColumns = [
    'nombre',
    'tipoUsuario',
    'direccion',
    'correo',
    'asentamiento',
    'status',
    'acciones'
  ];

  // Datos de la tabla
  idSucursal = '';
  data?: UserService;
  dataSource!: UserDataSource;
  index?: number;
  idUsuario?: string;

  // Datos de roles y filtros
  isAdmin = false;
  isSupervisor = false;
  isResponsable = false;
  user?: UserDetails;
  businessGroupId: string | null = null;
  branchGroupId: string | null = null;

  // ViewChildren
  @ViewChild(MatPaginator, {static: true})
  paginator!: MatPaginator;

  @ViewChild(MatSort, {static: true})
  sort!: MatSort;

  @ViewChild('filter', {static: true})
  filter?: ElementRef;

  @Input() control: string = 'self';

  // ═════════════════════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═════════════════════════════════════════════════════════════════════════════

  constructor(
    public httpClient: HttpClient,
    public dialog: MatDialog,
    public userService: UserService,
    private router: Router,
    private businessGroupService: BusinessGroupService,
    private branchGroupService: BranchGroupService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }
  ngOnInit() {
    // Paso 1: Inicializar roles
    this.initializeUserRoles();

    // Paso 2: Crear subscripcion combinada
    this.setupFiltersSubscription();
  }

  /**
   * Inicializa los roles del usuario
   */
  private initializeUserRoles(): void {
    this.isAdmin = this.authManagement.isUserAdmin();
    this.isSupervisor = this.authManagement.isUserPharmacySupervision();
    this.isResponsable = this.authManagement.isUserPharmacyResponsible();
  }

  private setupFiltersSubscription(): void {
    const userDetails = this.authManagement.userData();

    this.subs.sink = combineLatest([
      this.businessGroupService.businessGroupId$.pipe(
        distinctUntilChanged(),
      ),
      this.branchGroupService.branchGroupId$.pipe(
      )
    ]).pipe(
      debounceTime(300),
    ).subscribe(([businessGroupId, branchGroupId]) => {

      this.businessGroupId = businessGroupId;
      this.branchGroupId = branchGroupId;
      this.determineIdSucursal(userDetails);
      this.loadData();
    });
  }

  /**
   * Determina el idSucursal segun el rol del usuario
   *
   * LOGICA:
   * - ADMIN/SUPERVISOR: Usa el filtro del header (puede ser vacio para ver todos)
   * - RESPONSABLE: Sucursal fija (la suya)
   */
  private determineIdSucursal(userDetails: any): void {
    if (this.isSupervisor || this.isAdmin) {
      // Admin y Supervisor: usan el filtro del header
      this.idSucursal = this.branchGroupId || '';
    }

    if (this.isResponsable) {
      // Responsable: sucursal fija
      this.idSucursal = userDetails.IdSucursal;
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // METODOS PUBLICOS - ACCIONES
  // ═════════════════════════════════════════════════════════════════════════════

  /**
   * Refresca la tabla (reinicia ngOnInit)
   */
  refresh() {
    this.ngOnInit();
  }

  /**
   * Navega a crear nuevo usuario
   */
  addNew() {
    this.router.navigate(['admin/create-edit']).then();
  }

  /**
   * Edita un usuario existente
   */
  editCall(row: UserDetails) {
    const idUsuario = btoa(row.usr);
    const idGEMP = btoa(row.idGEMP);
    this.router.navigate(['admin/create-edit/' + idUsuario + '/' + idGEMP]).then();
  }

  /**
   * Navega al perfil de un usuario
   */
  goToProfile(row: UserDetails) {
    const idUsuario = btoa(row.usr);
    const idGEMP = btoa(row.idGEMP);
    this.router.navigate(['admin/profile/' + idUsuario + '/' + idGEMP]).then();
  }

  /**
   * Elimina un usuario
   */
  deleteItem(row: UserDetails) {
    this.idUsuario = row.idUsuario;

    const dialogRef = this.dialog.open(DoDeleteComponent, {
      data: {
        toDelete: 'Usuario',
        id: this.idUsuario,
        name: row.nombres,
        actionService: (id: string) => this.userService.deleteUser(id),
      }
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result === 1 && this.idUsuario) {
        this.removeFromTable(this.idUsuario);
      }
    });
  }

  /** Quita el usuario del BehaviorSubject y refresca la tabla/paginador */
  private removeFromTable(id: string) {
    const current = this.userService.dataChange.value ?? [];
    const updated = current.filter((x: UserDetails) => x.idUsuario !== id);

    this.userService.dataChange.next(updated);

    if (this.paginator) {
      this.paginator.firstPage();
    }

    if (this.filter?.nativeElement && this.dataSource) {
      this.dataSource.filter = this.filter.nativeElement.value ?? '';
    }
  }

  /**
   * Exporta los datos a Excel
   */
  exportExcel() {
    const exportData: Partial<TableElement>[] =
      this.dataSource.filteredData.map((x: UserDetails) => ({
        Nombre: x.nombres,
        Tipo: x.tipoUsuario,
        Telefono: x.movil,
        Direccion: x.domicilio,
        Municipio: x.municipio,
        CP: x.codigoPostal,
        Correo: x.email,
        Asentamiento: x.nombreAsentamiento,
        Sucursal: x.nombreSucursal,
        Estatus: x.status,
      }));
    TableExportUtil.exportToExcel(exportData, 'usuarios');
  }

  public loadData() {
    try {
      this.userService.dataChange.next([]);
      this.dataSource = new UserDataSource(
        this.businessGroupId,
        this.userService,
        this.paginator,
        this.sort,
        this.idSucursal || ''
      );

      if (this.filter?.nativeElement) {
        this.subs.sink = fromEvent(this.filter.nativeElement, 'keyup').subscribe(() => {
          if (!this.dataSource) {
            return;
          }
          this.dataSource.filter = this.filter?.nativeElement?.value ?? '';
        });
      }

      this.cdr.detectChanges();

    } catch (error) {
    }
  }

  private refreshTable() {
    this.paginator._changePageSize(this.paginator.pageSize);
  }
}
